import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { API_BASE } from "@/lib/api";
import { Link } from "react-router-dom";
import { Package2, Printer, Eye, Plus } from "lucide-react";
import { STATE_STYLE } from "@/components/StateBadge";

export default function ClienteBultos() {
  const [bultos, setBultos] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/bultos").then((r) => setBultos(r.data));
  }, []);

  const openDetail = async (id) => {
    const r = await api.get(`/bultos/${id}`);
    setDetail(r.data);
  };

  const downloadPDF = (id) => {
    const token = localStorage.getItem("voycargo_token");
    fetch(`${API_BASE}/bultos/${id}/label.pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => window.open(URL.createObjectURL(b), "_blank"));
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-2">/ Embalajes</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Mis bultos</h1>
          <p className="text-zinc-400 mt-2">Agrupaciones de pedidos con QR consolidado para el retiro rápido.</p>
        </div>
        <Link to="/cliente/masivo" className="bg-yellow-300 text-black font-bold px-5 py-3 uppercase tracking-widest text-sm hover:bg-yellow-200 inline-flex items-center gap-2" data-testid="btn-nuevo-bulto">
          <Plus className="w-4 h-4" /> Nuevo bulto (Excel)
        </Link>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="bg-zinc-900">
            <tr className="text-left text-[10px] font-mono-tech uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-3">Código</th>
              <th className="px-4 py-3">Pedidos</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Creado</th>
              <th className="px-4 py-3">Origen</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {bultos.map((b) => (
              <tr key={b.id} className="hover:bg-zinc-900/60" data-testid={`bulto-row-${b.code}`}>
                <td className="px-4 py-3 font-mono-tech text-sm font-bold text-yellow-300">{b.code}</td>
                <td className="px-4 py-3 font-display font-black text-2xl">{b.count}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] font-mono-tech uppercase tracking-wider ${b.state === "creado" ? "border-zinc-700 text-zinc-400" : "border-indigo-500/40 text-indigo-300 bg-indigo-500/10"}`}>
                    <span className="w-1 h-1 rounded-full bg-current" />
                    {b.state}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono-tech text-xs text-zinc-400">{new Date(b.created_at).toLocaleString("es-CL")}</td>
                <td className="px-4 py-3 text-xs text-zinc-400">{b.source === "bulk_import" ? "Carga Excel" : "Manual"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => openDetail(b.id)} className="w-8 h-8 border border-white/10 hover:border-yellow-300/40 hover:text-yellow-300 inline-flex items-center justify-center" title="Ver detalle" data-testid={`view-${b.code}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => downloadPDF(b.id)} className="w-8 h-8 border border-white/10 hover:border-yellow-300/40 hover:text-yellow-300 inline-flex items-center justify-center" title="PDF completo" data-testid={`pdf-${b.code}`}>
                      <Printer className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {bultos.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-16 text-center">
                <Package2 className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                <div className="text-zinc-500 font-mono-tech text-sm">Aún no tienes bultos. Los bultos se crean automáticamente cuando cargas pedidos por Excel.</div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="w-full max-w-3xl bg-zinc-950 border border-white/10 max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-white/10 flex items-start justify-between">
              <div>
                <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">/ Bulto</div>
                <div className="font-display font-black text-3xl tracking-tighter mt-1">{detail.code}</div>
                <div className="text-zinc-400 text-sm mt-1">{detail.count} pedidos · {new Date(detail.created_at).toLocaleString("es-CL")}</div>
              </div>
              <button onClick={() => setDetail(null)} className="text-zinc-400 hover:text-white text-2xl">×</button>
            </div>
            <div className="overflow-y-auto p-6">
              <div className="grid grid-cols-1 gap-3">
                {detail.orders?.map((o) => (
                  <div key={o.id} className="border border-white/10 p-4 bg-zinc-900/40 flex items-center gap-4">
                    <span className="font-mono-tech text-xs text-yellow-300">{o.tracking_number}</span>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{o.destinatario.nombre}</div>
                      <div className="text-xs text-zinc-400">{o.destinatario.direccion}, {o.destinatario.comuna}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] font-mono-tech uppercase tracking-wider ${STATE_STYLE[o.state]?.color}`}>
                      <span className="w-1 h-1 rounded-full bg-current" />
                      {STATE_STYLE[o.state]?.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end">
              <button onClick={() => downloadPDF(detail.id)} className="bg-yellow-300 text-black font-bold px-6 py-3 uppercase tracking-widest text-sm hover:bg-yellow-200 inline-flex items-center gap-2">
                <Printer className="w-4 h-4" /> Descargar PDF completo
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
